import api from './api';

export interface Watchlist {
  id: number;
  userId: number;
  name: string;
  symbols: Array<{ symbol: string; exchange: string }>;
  /** 'FAVOURITES' = the system "★ My favourites" list (mirrors starred symbols; read-only). */
  systemKind?: string | null;
  createdAt: string;
  updatedAt: string;
}

// Legacy alias kept for existing imports
export type StrategyWatchlist = Watchlist;

export type ScanPhase = 'QUEUED' | 'FETCHING_HISTORY' | 'EVALUATING' | 'PERSISTING' | 'DONE';
export type PerSymbolScanStatus =
  | 'PENDING' | 'FETCHING' | 'EVALUATING'
  | 'SIGNAL' | 'CLEAN' | 'SKIPPED_INSUFFICIENT_DATA' | 'ERROR';

export interface PerSymbolStatusEntry {
  symbol: string;
  exchange: string;
  status: PerSymbolScanStatus;
  message?: string;
  signalAction?: string;
  updatedAt?: string;
}

export interface StrategyScanJob {
  id: number;
  strategyId: number;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';
  intervalMinutes: number;
  totalSymbols: number;
  processedSymbols: number;
  signalsFound: number;
  error?: string;
  createdAt: string;
  completedAt?: string;
  // Live progress
  currentSymbol?: string;
  phase?: ScanPhase;
  perSymbolStatus?: PerSymbolStatusEntry[];
  isScheduled?: boolean;
  scheduledScanId?: number;
}

export interface StrategySignalItem {
  id: number;
  strategyId: number;
  symbol: string;
  exchange: string;
  action: string;
  intervalMinutes: number;
  strength?: string;
  entryPrice?: number;
  stopLoss?: number;
  target?: number;
  currentPrice?: number;
  isManualScan: boolean;
  computationData?: any;
  candleTime?: string;
  alertId?: number;
  scheduledScanId?: number;
  createdAt: string;
  // Forward-performance analytics (R97) — refreshed each scan from the last
  // candle while the signal is active.
  maxGainPct?: number;
  maxGainPrice?: number;
  maxGainAt?: string;
  maxAdversePct?: number;
  barsTracked?: number;
  trackingActive?: boolean;
}

export interface StrategyConfigUpdate {
  name?: string;
  description?: string;
  configuration?: any;
}

export type ScheduleType = 'MINUTE' | 'HOUR' | 'DAY';

export interface ScheduledScan {
  id: number;
  userId: number;
  strategyId: number;
  watchlistId: number;
  intervalMinutes: number;
  scheduleType: ScheduleType;
  scheduleValue: number;
  scheduleTimeOfDay?: string | null;
  enabled: boolean;
  routeDashboard: boolean;
  routeTelegram: boolean;
  /** TradeDesk — per-scan Auto-Trade switch. */
  autoTrade?: boolean;
  lastRunAt?: string | null;
  lastRunStatus?: 'SUCCESS' | 'FAILURE' | 'RUNNING' | null;
  lastRunError?: string | null;
  lastSignalsFound?: number | null;
  lastScanJobId?: number | null;
  nextRunAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertSchedulePayload {
  watchlistId: number;
  intervalMinutes: number;
  scheduleType: ScheduleType;
  scheduleValue: number;
  scheduleTimeOfDay?: string | null;
  enabled?: boolean;
  routeDashboard?: boolean;
  routeTelegram?: boolean;
  autoTrade?: boolean;
}

// Backend wraps responses in { success, data } — unwrap on the client.
export const strategyScanService = {
  // ── User-scoped Watchlists ────────────────────────────────────────────
  /** includeFavourites: also return the system "★ My favourites" list (first). */
  listWatchlists: (opts?: { includeFavourites?: boolean }) =>
    api.get<any>(`/watchlists`, opts?.includeFavourites ? { params: { includeFavourites: 1 } } : undefined).then(r => (r.data?.data as Watchlist[]) || []),
  createWatchlist: (data: { name: string; symbols?: Array<{ symbol: string; exchange: string }> }) =>
    api.post<any>(`/watchlists`, data).then(r => r.data?.data as Watchlist),
  updateWatchlist: (id: number, data: { name?: string; symbols?: Array<{ symbol: string; exchange: string }> }) =>
    api.put<any>(`/watchlists/${id}`, data).then(r => r.data?.data as Watchlist),
  deleteWatchlist: (id: number) =>
    api.delete<any>(`/watchlists/${id}`).then(r => r.data),
  addSymbolToWatchlist: (id: number, s: { symbol: string; exchange: string }) =>
    api.post<any>(`/watchlists/${id}/symbols`, s).then(r => r.data?.data as Watchlist),
  removeSymbolFromWatchlist: (id: number, s: { symbol: string; exchange: string }) =>
    api.delete<any>(`/watchlists/${id}/symbols`, { params: s }).then(r => r.data?.data as Watchlist),

  // ── Scanning ──────────────────────────────────────────────────────────
  startScan: (
    strategyId: number,
    data: { watchlistId?: number; intervalMinutes: number; symbols?: Array<{ symbol: string; exchange: string }>; asOfDate?: string }
  ) =>
    api.post<any>(`/strategies/${strategyId}/scan`, data).then(r => r.data),
  getScanJobs: (strategyId: number) =>
    api.get<any>(`/strategies/${strategyId}/scan-jobs`).then(r => r.data),
  getScanJob: (strategyId: number, jobId: number) =>
    api.get<any>(`/strategies/${strategyId}/scan-jobs/${jobId}`).then(r => r.data?.data as StrategyScanJob),

  // ── Signals ───────────────────────────────────────────────────────────
  getSignals: (
    strategyId: number,
    params?: { days?: number; candleDate?: string; symbol?: string; action?: string; actions?: string; intervalMinutes?: number; exchange?: string; page?: number; limit?: number }
  ) =>
    api.get<any>(`/strategies/${strategyId}/signals`, { params }).then(r => r.data),
  getSignalById: (strategyId: number, id: number) =>
    api.get<any>(`/strategies/${strategyId}/signals/${id}`).then(r => r.data),
  /** F-Strategies-RefreshPerf — manual refresh of LTP/MaxGain/MaxAdv for a
   *  capped batch of visible signal ids. Returns the updated rows so the
   *  caller can splice them back into the displayed list. */
  refreshSignalsPerformance: (strategyId: number, signalIds: number[]) =>
    api.post<any>(`/strategies/${strategyId}/signals/refresh-performance`, { signalIds })
      .then(r => (r.data?.data as StrategySignalItem[]) || []),
  purgeSignals: (
    strategyId: number,
    params?: { olderThanDays?: number; intervalMinutes?: number; action?: string; symbol?: string }
  ) =>
    api.delete<any>(`/strategies/${strategyId}/signals`, { params }).then(r => r.data?.data as { deleted: number }),

  // ── Scheduled Scans ───────────────────────────────────────────────────
  // Legacy singular endpoints — kept so the StrategiesPage tile-status
  // lookup keeps working without churn. New multi-schedule UI uses the
  // plural list/create + per-id update/toggle/delete below.
  getSchedule: (strategyId: number) =>
    api.get<any>(`/strategies/${strategyId}/scheduled-scan`).then(r => (r.data?.data as ScheduledScan | null) || null),
  upsertSchedule: (strategyId: number, payload: UpsertSchedulePayload) =>
    api.post<any>(`/strategies/${strategyId}/scheduled-scan`, payload).then(r => r.data?.data as ScheduledScan),
  toggleSchedule: (strategyId: number, enabled: boolean) =>
    api.post<any>(`/strategies/${strategyId}/scheduled-scan/toggle`, { enabled }).then(r => r.data?.data as ScheduledScan),
  deleteSchedule: (strategyId: number) =>
    api.delete<any>(`/strategies/${strategyId}/scheduled-scan`).then(r => r.data),

  // F-Strategies-MultiSchedule — list/create/update/toggle/delete per row.
  listSchedules: (strategyId: number) =>
    api.get<any>(`/strategies/${strategyId}/scheduled-scans`)
      .then(r => ((r.data?.data as ScheduledScan[]) || [])),
  /** Create a NEW schedule for this strategy. The backend appends a row
   *  even when other schedules already exist (allowAppend flag). */
  createSchedule: (strategyId: number, payload: UpsertSchedulePayload) =>
    api.post<any>(`/strategies/${strategyId}/scheduled-scans`, { ...payload, allowAppend: true })
      .then(r => r.data?.data as ScheduledScan),
  /** Update an existing schedule by id. */
  updateScheduleById: (strategyId: number, id: number, payload: UpsertSchedulePayload) =>
    api.put<any>(`/strategies/${strategyId}/scheduled-scans/${id}`, { ...payload, id })
      .then(r => r.data?.data as ScheduledScan),
  toggleScheduleById: (strategyId: number, id: number, enabled: boolean) =>
    api.post<any>(`/strategies/${strategyId}/scheduled-scans/${id}/toggle`, { enabled })
      .then(r => r.data?.data as ScheduledScan),
  deleteScheduleById: (strategyId: number, id: number) =>
    api.delete<any>(`/strategies/${strategyId}/scheduled-scans/${id}`).then(r => r.data),

  // ── Strategy configuration (RockBand thresholds etc.) ──────────────────
  getStrategy: (strategyId: number) =>
    api.get<any>(`/strategies/${strategyId}`).then(r => (r.data?.strategy || r.data?.data || r.data) as any),
  updateStrategy: (strategyId: number, payload: StrategyConfigUpdate & { name?: string }) =>
    api.put<any>(`/strategies/${strategyId}`, payload).then(r => (r.data?.strategy || r.data?.data || r.data) as any),
};
